import React, { useEffect, useRef, useState } from "react";
import { getAttendance, getEmployees, getLeaders, bulkCheckIn, getInterns, updateAttendence } from "../../http";
import Loading from "../Loading";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import swal from "sweetalert";

const AttendanceView = () => {
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [attendance, setAttendance] = useState();
  const [employeeMap, setEmployeeMap] = useState();
  const [employees, setEmployees] = useState();
  const [selectedEmployee, setSelectedEmployee] = useState();
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    present: 0,
    halfday: 0,
    absent: 0,
  });

  const years = [2020, 2021, 2022, 2023, 2024, 2025]; // Customize this as needed
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthDays = {
    January: 31,
    February: 28,
    March: 31,
    April: 30,
    May: 31,
    June: 30,
    July: 31,
    August: 31,
    September: 30,
    October: 31,
    November: 30,
    December: 31,
  };
  const numOfDays = monthDays[selectedMonth];
  const days = Array.from({ length: numOfDays || 0 }, (_, index) => index + 1);

  useEffect(() => {
    const dt = new Date();
    const currentYear = dt.getFullYear();
    const currentMonth = months[dt.getMonth()];
    const currentDate = dt.getDate();

    // Set initial filter values
    setSelectedYear(currentYear);
    setSelectedMonth(currentMonth);
    setSelectedDay(currentDate);

    let empObj = {};
    const fetchData = async () => {
      const obj = {
        year: currentYear,
        month: dt.getMonth() + 1,
        date: currentDate,
      };
      const res = await getAttendance(obj);
      const { data } = res;
      setAttendance(data);
      calculateStats(data);
    };

    const fetchEmployees = async () => {
      const emps = await getEmployees();
      const leaders = await getLeaders();
      const interns = await getInterns();
      emps.data.forEach(
        (employee) => (empObj[employee.id] = [employee.name, employee.email])
      );
      leaders.data.forEach(
        (leader) => (empObj[leader.id] = [leader.name, leader.email])
      );
      if (interns?.data) {
        interns.data.forEach(
          (intern) => (empObj[intern.id] = [intern.name, intern.email])
        );
      }
      setEmployeeMap(empObj);
      setEmployees([...emps.data, ...leaders.data, ...(interns?.data || [])]);
    };

    fetchEmployees();
    fetchData();
    // eslint-disable-next-line
  }, []);

  const calculateStats = (attendanceData) => {
    if (!attendanceData) return;

    let presentCount = 0;
    let halfdayCount = 0;
    let absentCount = 0;

    attendanceData.forEach((record) => {
      if (record.checkOutTime && record.checkInTime) {
        const hoursWorked =
          (new Date(record.checkOutTime) - new Date(record.checkInTime)) /
          3600000;
        if (hoursWorked < 4) {
          absentCount++;
        } else if (hoursWorked < 9) {
          halfdayCount++;
        } else {
          presentCount++;
        }
      } else {
        absentCount++;
      }
    });

    setStats({
      total: attendanceData.length,
      present: presentCount,
      halfday: halfdayCount,
      absent: absentCount,
    });
  };

  const searchAttendance = async () => {
    const obj = {};
    if (selectedEmployee) {
      obj["employeeID"] = selectedEmployee;
    }
    if (selectedYear) {
      obj["year"] = selectedYear;
    }
    if (selectedMonth) {
      obj["month"] = months.findIndex((month) => month === selectedMonth) + 1;
    }
    if (selectedDay) {
      obj["date"] = selectedDay;
    }

    console.log(obj);

    const res = await getAttendance(obj);
    const { data } = res;
    setAttendance(data);
    calculateStats(data);
  };

  const getStatusLabel = (record) => {
    if (!record.checkInTime) return "Absent";
    if (!record.checkOutTime) return "Absent";

    const hoursWorked =
      (new Date(record.checkOutTime) - new Date(record.checkInTime)) / 3600000;
    if (hoursWorked < 4) {
      return "Absent";
    } else if (hoursWorked < 9) {
      return "Halfday";
    } else {
      return "Present";
    }
  };

  const getStatusClassName = (status) => {
    switch (status) {
      case "Present":
        return "text-success";
      case "Halfday":
        return "text-warning";
      case "Absent":
        return "text-danger";
      default:
        return "";
    }
  };

  const handleMarkAttendance = (record) => {
    const dateStr = `${record.year}-${String(record.month).padStart(2, "0")}-${String(record.date).padStart(2, "0")}`;

    swal("Update Attendance", {
      buttons: {
        cancel: "Cancel",
        present: {
          text: "Present",
          value: 2,
        },
        halfday: {
          text: "Halfday",
          value: 1,
        },
        absent: {
          text: "Absent",
          value: 0,
        },
      },
    }).then(async (value) => {
      if (value === null) return;

      try {
        const res = await updateAttendence({
          employeeID: record.employeeID,
          dateC: dateStr,
          present: value,
        });

        if (res.success) {
          toast.success(res.message || "Attendance updated");
          await searchAttendance();
        } else {
          toast.error(res.message || "Failed to update attendance");
        }
      } catch (error) {
        toast.error("Error updating attendance");
      }
    });
  };

  const fileInputRef = useRef(null);

  const convertTime = (dateString, timeString) => {
    if (!dateString || !timeString) return null;

    if (typeof timeString === "string" && timeString.match(/^\d{1,2}:\d{2}$/)) {
      const [hours, minutes] = timeString.split(":").map(Number);

      const localDate = new Date(dateString);
      localDate.setHours(hours, minutes, 0, 0);

      return localDate.toISOString(); // Convert to UTC
    }

    return null;
  };

  const convertDate = (value) => {
    if (!value) return null;

    if (typeof value === "number") {
      const excelDate = new Date((value - 25569) * 86400000);
      return excelDate.toISOString().split("T")[0];
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) return null;

    return date.toISOString().split("T")[0];
  };

  const handleSubmit = async () => {
    const file = fileInputRef.current?.files[0];
    if (!file) {
      toast.error("Please upload a file.");
      return;
    }

    setUploading(true);

    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = async (e) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      let jsonData = XLSX.utils.sheet_to_json(sheet);

      jsonData = jsonData.map((record) => ({
        username: record["username"]?.trim(),
        date: convertDate(record["date"]),
        checkIn: convertTime(convertDate(record["date"]), record["checkIn"]),
        checkOut: convertTime(convertDate(record["date"]), record["checkOut"]),
      }));

      const validRecords = jsonData.filter(
        (record) => record.username && record.date && record.checkIn && record.checkOut
      );

      if (validRecords.length === 0) {
        toast.error("Invalid or empty records in the file.");
        setUploading(false);
        return;
      }
      try {
        const response = await bulkCheckIn({ "attendanceRecords": validRecords });

        if (response.success) {
          toast.success(response.message || "Bulk check-in successful!");
          searchAttendance({});
          setUploading(false);
        } else {
          toast.error(response.error || "Bulk check-in failed!");
          setUploading(false);
        }
      } catch (error) {
        toast.error("Error processing file.");
        setUploading(false);
      };
    };
  }

  return (
    <>
      {attendance ? (
        <div className="main-content">
          <section className="section">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h4>Attendance</h4>
                <div className="d-flex gap-2">
                  <CSVLink
                    data={[
                      [
                        "index",
                        "Name",
                        "Email",
                        "Date",
                        "Day",
                        "Status",
                        "CheckIn Time",
                        "CheckOut Time",
                        "Camera/Geo Location",
                        "GMeet",
                        "Emotion",
                      ],
                      ...attendance?.map((att, index) => [
                        index + 1,
                        employeeMap?.[att.employeeID]?.[0] || "N/A",
                        employeeMap?.[att.employeeID]?.[1] || "N/A",
                        `${att.date}-${att.month}-${att.year}`,
                        att.day,
                        getStatusLabel(att),
                        att.checkInTime
                          ? new Date(att.checkInTime).toLocaleTimeString()
                          : "N/A",
                        att.checkOutTime
                          ? new Date(att.checkOutTime).toLocaleTimeString()
                          : "N/A",
                        att.camera ? "Camera" : att.withinRange ? "Geo Location" : "NA",
                        att.gmeet ? "Yes" : "No",
                        att?.camera ? att?.emotion || "N/A" : "N/A"
                      ]),
                    ]}
                    filename={`attendance_${new Date().toISOString().split("T")[0]
                      }.csv`}
                    className="btn btn-success m-2"
                    target="_blank">
                    Download CSV
                  </CSVLink>
                  <label className="btn btn-success m-2">
                    {uploading ? "Uploading..." : "Upload File"}
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleSubmit}
                      ref={fileInputRef}
                      className="d-none"
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="d-flex flex-wrap justify-content-center w-100">
              <div className="col-md col-12 mb-2">
                <select
                  className="form-control select2 w-100"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}>
                  <option value="">Employees</option>
                  {employees?.map((employee) => (
                    <option key={employee._id} value={employee.id}>
                      {employee.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md col-12 mb-2">
                <select
                  className="form-control select2 w-100"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}>
                  <option value="">Year</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md col-12 mb-2">
                <select
                  className="form-control select2 w-100"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}>
                  <option value="">Month</option>
                  {months.map((month) => (
                    <option key={month} value={month}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md col-12 mb-2">
                <select
                  className="form-control select2 w-100"
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(e.target.value)}>
                  <option value="">Day</option>
                  {days.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={searchAttendance}
                className="btn btn-lg btn-primary col-md col-12 mb-2">
                Search
              </button>
            </div>
          </section>

          {/* Summary Statistics Section */}
          {(selectedMonth || selectedEmployee) && (
            <div className="card mt-4">
              <div className="card-header">
                <h5>Attendance Summary</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3 col-sm-6">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h5 className="card-title">Total Records</h5>
                        <h3>{stats.total}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div className="card bg-success text-white">
                      <div className="card-body text-center">
                        <h5 className="card-title">Full Days</h5>
                        <h3>{stats.present}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div className="card bg-warning text-white">
                      <div className="card-body text-center">
                        <h5 className="card-title">Half Days</h5>
                        <h3>{stats.halfday}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div className="card bg-danger text-white">
                      <div className="card-body text-center">
                        <h5 className="card-title">Absent</h5>
                        <h3>{stats.absent}</h3>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  {selectedEmployee &&
                    employeeMap &&
                    employeeMap[selectedEmployee] && (
                      <p>
                        <strong>Employee:</strong>{" "}
                        {employeeMap[selectedEmployee][0]}
                      </p>
                    )}
                  {selectedMonth && (
                    <p>
                      <strong>Month:</strong> {selectedMonth}
                    </p>
                  )}
                  {selectedYear && (
                    <p>
                      <strong>Year:</strong> {selectedYear}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-striped table-md center-text">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  {/* <th>Email</th> */}
                  <th>Date</th>
                  <th>Day</th>
                  <th>Status</th>
                  <th>CheckIn Time</th>
                  <th>CheckOut Time</th>
                  <th>Camera/Geo Location</th>
                  <th>GMeet</th>
                  <th>Emotion</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {attendance?.map((record, idx) => {
                  const status = getStatusLabel(record);
                  return (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>
                        {employeeMap && employeeMap[record.employeeID]
                          ? employeeMap[record.employeeID][0]
                          : "N/A"}
                      </td>
                      {/* <td>
                        {employeeMap && employeeMap[record.employeeID]
                          ? employeeMap[record.employeeID][1]
                          : "N/A"}
                      </td> */}
                      <td>
                        {record.date + "-" + record.month + "-" + record.year}
                      </td>
                      <td>{record.day}</td>
                      <td className={getStatusClassName(status)}>{status}</td>
                      <td>
                        {record.checkInTime
                          ? new Date(record.checkInTime).toLocaleTimeString()
                          : ""}
                      </td>
                      <td>
                        {record.checkOutTime
                          ? new Date(record.checkOutTime).toLocaleTimeString()
                          : ""}
                      </td>
                      <td>
                        {record.camera
                          ? "Camera"
                          : record.withinRange
                            ? "Geo Location"
                            : "NA"}
                      </td>
                      <td>{record.gmeet ? "Yes" : "No"}</td>
                      <td>{record?.camera ? record?.emotion || "N/A" : "N/A"}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleMarkAttendance(record)}
                        >
                          Mark
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <Loading />
      )}
    </>
  );
};

export default AttendanceView;